import { useEffect, useRef } from 'react'
import { communicationBridge } from '@core/bridge/communicationBridge'

/**
 * Generic React binding for the communication bridge. Registers a connection for
 * the lifetime of the mount (given a contract + iframe ref) and returns a bound
 * `send`. Routing, origin checks and the envelope all live in the transport +
 * the contract — this is only the React glue.
 *
 * The inbound handler is read through a ref so a fresh inline `onEvent` each
 * render doesn't re-register the connection.
 */
export const useFrameBridge = ({ id, iframeRef, allowedOrigin, contract, onEvent, enabled = true }) => {
  const onEventRef = useRef(onEvent)
  useEffect(() => {
    onEventRef.current = onEvent
  })

  useEffect(() => {
    if (!enabled || !id || !contract) return undefined

    communicationBridge.connect({
      id,
      getContentWindow: () => iframeRef?.current?.contentWindow ?? null,
      allowedOrigin,
      match: contract.match,
      parse: contract.parse,
      build: contract.build,
      onMessage: (type, payload, meta) => onEventRef.current?.(type, payload, meta),
    })

    return () => communicationBridge.disconnect(id)
  }, [id, allowedOrigin, contract, enabled, iframeRef])

  return {
    send: (type, payload) => communicationBridge.send(id, type, payload),
  }
}
